import { Alert, AlertColor, AlertTitle, Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { PrognosisWarningDto, PrognosisWarningSeverity } from '../ensan-steckbrief.dto';
import { affectedChips, warningMetricLabels, warningTexts } from '../steckbrief-calculation-messages';

/**
 * MUI severity per backend severity: a WARNING says the number stands on
 * inputs the reference set cannot support; CHECK (verify an input) and
 * SUPPRESSED (the metric is deliberately not predicted) are information.
 * Unknown (future) severities read as a warning — never quieter than meant.
 */
const ALERT_SEVERITY: Record<PrognosisWarningSeverity, AlertColor> = {
    WARNING: 'warning',
    CHECK: 'info',
    SUPPRESSED: 'info',
};

/** i18next keys of the severity marker next to the title (WARNING needs none — the alert colour says it). */
const SEVERITY_LABEL_KEYS: Partial<Record<PrognosisWarningSeverity, string>> = {
    CHECK: 'Check',
    SUPPRESSED: 'Not predicted',
};

/** One labelled text line of a warning ("What it means: …"). */
function WarningLine({ label, text, testId }: { label: string; text: string; testId: string }) {
    return (
        <Typography variant="body2" data-testid={testId}>
            <Box component="strong" sx={{ fontWeight: 600 }}>
                {label}:
            </Box>{' '}
            {text}
        </Typography>
    );
}

/**
 * "Warnhinweise zur Prognose" (GEN-788): the applicability warnings of the
 * calibrated prognosis model for THIS `/calculate` run, one alert per warning
 * in the backend's display order — title, when it fired (live values and the
 * tested threshold), what it means, what to do, the affected form field /
 * measure and the metrics it questions. A warning never stops a prediction; it
 * says how much weight the number can carry (the sheet's Warnings tab).
 *
 * `role="note"` on purpose: `/calculate` re-runs on every edit, and a live
 * `role="alert"` would make screen readers re-announce every warning each time.
 *
 * `undefined` (a backend predating GEN-788) or `null` renders NOTHING — claiming
 * "no warnings" would be dishonest; `[]` renders the explicit none line. The
 * disclaimer always shows: no warning does not make a prediction accurate.
 * Form only — never printed, never stamped.
 */
export function SteckbriefPrognosisWarnings({ warnings }: { warnings: PrognosisWarningDto[] | null | undefined }) {
    const { t, i18n } = useTranslation();
    if (warnings == null) return null;

    return (
        <Box data-testid="prognosis-warnings">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('Prognosis warnings')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                {t(
                    'A warning never stops a prediction — it tells you how much weight the number can carry. Read it before quoting a figure to a municipality.',
                )}
            </Typography>
            {warnings.length === 0 ? (
                <Typography variant="body2" data-testid="prognosis-warnings-none">
                    {t('No warnings — this building sits inside the reference set.')}
                </Typography>
            ) : (
                <Stack spacing={1} data-testid="prognosis-warnings-list">
                    {warnings.map((warning, index) => {
                        const texts = warningTexts(t, i18n.language, warning);
                        const severityLabelKey = SEVERITY_LABEL_KEYS[warning.severity];
                        const chips = affectedChips(t, warning);
                        const metrics = warningMetricLabels(t, warning);
                        return (
                            <Alert
                                // A code fires at most once per run today — the position
                                // keeps the key unique should that ever change.
                                key={`${warning.code}-${index}`}
                                severity={ALERT_SEVERITY[warning.severity] ?? 'warning'}
                                role="note"
                                data-severity={warning.severity}
                                data-testid={`prognosis-warning-${warning.code}`}
                            >
                                <AlertTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <span data-testid={`prognosis-warning-${warning.code}-title`}>{texts.title}</span>
                                    {severityLabelKey && (
                                        <Chip size="small" variant="outlined" label={t(severityLabelKey)} />
                                    )}
                                </AlertTitle>
                                <Stack spacing={0.5}>
                                    {texts.trigger !== null && (
                                        <WarningLine
                                            label={t('When it fires')}
                                            text={texts.trigger}
                                            testId={`prognosis-warning-${warning.code}-trigger`}
                                        />
                                    )}
                                    {texts.meaning !== null && (
                                        <WarningLine
                                            label={t('What it means')}
                                            text={texts.meaning}
                                            testId={`prognosis-warning-${warning.code}-meaning`}
                                        />
                                    )}
                                    {texts.action !== null && (
                                        <WarningLine
                                            label={t('What to do')}
                                            text={texts.action}
                                            testId={`prognosis-warning-${warning.code}-action`}
                                        />
                                    )}
                                    {(chips.length > 0 || metrics.length > 0) && (
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            useFlexGap
                                            sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                                            data-testid={`prognosis-warning-${warning.code}-affects`}
                                        >
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {t('Concerns')}:
                                            </Typography>
                                            {chips.map(({ key, label }) => (
                                                <Chip key={key} size="small" variant="outlined" label={label} />
                                            ))}
                                            {metrics.map((label) => (
                                                <Chip key={`metric-${label}`} size="small" label={label} />
                                            ))}
                                        </Stack>
                                    )}
                                </Stack>
                            </Alert>
                        );
                    })}
                </Stack>
            )}
            <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}
                data-testid="prognosis-warnings-disclaimer"
            >
                {t(
                    'No warning does NOT mean the prediction is accurate. It means the inputs sit inside the range the reference set covers. The bands still apply: they are 10th–90th percentile, so one building in five falls outside them even when nothing is flagged.',
                )}
            </Typography>
        </Box>
    );
}
